import { useState, useEffect, useCallback } from 'react';
import supabase, { orgId } from '../supabaseClient';

export default function usePLUpload() {
  const [uploads, setUploads]   = useState([]);
  const [accounts, setAccounts] = useState([]); // discovered nominal accounts
  const [mappings, setMappings] = useState({}); // { accountName: { type, targetId, targetLabel } }
  const [actuals,  setActuals]  = useState({}); // { 'YYYY-MM': { accountName: value } }
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading]   = useState(true);

  const load = useCallback(async () => {
    const [{ data: acc }, { data: map }, { data: act }, { data: snaps }, { data: ups }] = await Promise.all([
      supabase.from('pl_nominal_accounts').select('*').eq('org_id', orgId).order('account_name'),
      supabase.from('pl_nominal_mappings').select('*').eq('org_id', orgId),
      supabase.from('pl_actuals').select('*').eq('org_id', orgId),
      supabase.from('pl_snapshots').select('id,label,snapshot_at,months_affected').eq('org_id', orgId).order('snapshot_at', { ascending: false }).limit(10),
      supabase.from('pl_uploads').select('id,uploaded_at,filename,months_covered').eq('org_id', orgId).order('uploaded_at', { ascending: false }).limit(20),
    ]);

    setAccounts(acc || []);

    const mapObj = {};
    (map || []).forEach(m => { mapObj[m.account_name] = { type: m.mapping_type, targetId: m.mapping_target_id, targetLabel: m.mapping_target_label }; });
    setMappings(mapObj);

    const actObj = {};
    (act || []).forEach(a => {
      if (!actObj[a.month_key]) actObj[a.month_key] = {};
      actObj[a.month_key][a.account_name] = a.value;
    });
    setActuals(actObj);

    setSnapshots(snaps || []);
    setUploads(ups || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Parse Xero P&L Excel file
  const parseXeroPL = useCallback((arrayBuffer) => {
    // Use SheetJS to parse
    const XLSX = window.XLSX;
    if (!XLSX) return null;
    const wb = XLSX.read(arrayBuffer, { type: 'array' });
    const sheet = wb.Sheets[wb.SheetNames[0]];
    const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });

    // Find header row (contains 'Account' and month columns)
    let headerRow = -1;
    for (let i = 0; i < Math.min(10, rows.length); i++) {
      if (rows[i][0] === 'Account' || String(rows[i][0]).includes('Account')) {
        headerRow = i; break;
      }
    }
    if (headerRow === -1) return null;

    const headers = rows[headerRow];
    // Parse month headers — format "Apr 2026"
    const monthCols = [];
    const MN = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    for (let c = 1; c < headers.length; c++) {
      const h = String(headers[c]).trim();
      if (!h) continue;
      const parts = h.split(' ');
      if (parts.length === 2) {
        const mIdx = MN.indexOf(parts[0]);
        const yr = parseInt(parts[1]);
        if (mIdx >= 0 && !isNaN(yr)) {
          const key = `${yr}-${String(mIdx + 1).padStart(2, '0')}`;
          monthCols.push({ col: c, key, label: h });
        }
      }
    }

    // Parse rows
    const SECTION_MARKERS = ['Turnover', 'Cost of Sales', 'Administrative Costs'];
    const SKIP_PREFIXES = ['Total ', 'Gross ', 'Operating ', 'Profit ', 'Net '];
    let currentSection = '';
    const parsedAccounts = [];

    for (let r = headerRow + 1; r < rows.length; r++) {
      const row = rows[r];
      const name = String(row[0] || '').trim();
      if (!name) continue;

      // Section header
      if (SECTION_MARKERS.includes(name)) { currentSection = name; continue; }
      // Skip total/summary rows
      if (SKIP_PREFIXES.some(p => name.startsWith(p))) continue;
      // Skip empty data rows
      const hasData = monthCols.some(mc => row[mc.col] !== '' && row[mc.col] !== 0);
      if (!hasData) continue;

      const values = {};
      monthCols.forEach(mc => { values[mc.key] = parseFloat(row[mc.col]) || 0; });
      parsedAccounts.push({ name, section: currentSection, values });
    }

    return { accounts: parsedAccounts, months: monthCols.map(mc => mc.key) };
  }, []);

  // Upload a P&L file
  const uploadPL = useCallback(async (arrayBuffer, filename, ohValues, ohCategories) => {
    const parsed = parseXeroPL(arrayBuffer);
    if (!parsed) throw new Error('Could not parse file — check it is a Xero P&L export');

    // Take snapshot of current overhead values before overwriting
    const label = `Before upload: ${filename}`;
    const snap = { org_id: orgId, label, overhead_values: ohValues, months_affected: parsed.months };
    await supabase.from('pl_snapshots').insert(snap);

    // Enforce max 10 snapshots
    const { data: allSnaps } = await supabase.from('pl_snapshots').select('id,snapshot_at').eq('org_id', orgId).order('snapshot_at', { ascending: true });
    if (allSnaps && allSnaps.length > 10) {
      const toDelete = allSnaps.slice(0, allSnaps.length - 10).map(s => s.id);
      await supabase.from('pl_snapshots').delete().in('id', toDelete);
    }

    // Store raw upload
    const { data: uploadRow } = await supabase.from('pl_uploads').insert({
      org_id: orgId, filename, months_covered: parsed.months,
      raw_data: parsed.accounts,
    }).select('id').single();
    const uploadId = uploadRow?.id;

    // Upsert nominal accounts
    const accRows = parsed.accounts.map(a => ({ org_id: orgId, account_name: a.name, section: a.section }));
    await supabase.from('pl_nominal_accounts').upsert(accRows, { onConflict: 'org_id,account_name', ignoreDuplicates: true });

    // Upsert actuals
    const actRows = [];
    parsed.accounts.forEach(acc => {
      Object.entries(acc.values).forEach(([month_key, value]) => {
        actRows.push({ org_id: orgId, account_name: acc.name, month_key, value, upload_id: uploadId });
      });
    });
    for (let i = 0; i < actRows.length; i += 500) {
      await supabase.from('pl_actuals').upsert(actRows.slice(i, i + 500), { onConflict: 'org_id,account_name,month_key' });
    }

    await load();
    return parsed;
  }, [parseXeroPL, load]);

  // Save a mapping
  const saveMapping = useCallback(async (accountName, type, targetId, targetLabel) => {
    await supabase.from('pl_nominal_mappings').upsert(
      { org_id: orgId, account_name: accountName, mapping_type: type, mapping_target_id: targetId, mapping_target_label: targetLabel, updated_at: new Date().toISOString() },
      { onConflict: 'org_id,account_name' }
    );
    setMappings(prev => ({ ...prev, [accountName]: { type, targetId, targetLabel } }));
  }, []);

  // Restore a snapshot
  const restoreSnapshot = useCallback(async (snapshotId) => {
    const { data } = await supabase.from('pl_snapshots').select('overhead_values').eq('id', snapshotId).single();
    return data?.overhead_values || null;
  }, []);

  // Get actuals-applied overhead values for a given month
  const getActualsForMonth = useCallback((monthKey) => {
    const monthActuals = actuals[monthKey] || {};
    const result = {}; // { subCategoryId: totalValue }
    Object.entries(monthActuals).forEach(([accountName, value]) => {
      const mapping = mappings[accountName];
      if (mapping?.type === 'overhead_sub' && mapping.targetId) {
        result[mapping.targetId] = (result[mapping.targetId] || 0) + value;
      }
    });
    return result;
  }, [actuals, mappings]);

  return {
    uploads, accounts, mappings, actuals, snapshots, loading,
    uploadPL, saveMapping, restoreSnapshot, getActualsForMonth, reload: load,
  };
}
